import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Calendar as CalendarIcon, Grid3x3, List, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../AuthContext";
import ShiftWeekGrid from "../components/shift/ShiftWeekGrid";
import ShiftMonthCalendar from "../components/shift/ShiftMonthCalendar";
import ShiftListView from "../components/shift/ShiftListView";
import ShiftDialog from "../components/shift/ShiftDialog";

const API = process.env.REACT_APP_BACKEND_URL;

function startOfWeek(d) {
    const dt = new Date(d);
    const day = (dt.getDay() + 6) % 7; // Monday=0
    dt.setDate(dt.getDate() - day);
    dt.setHours(0, 0, 0, 0);
    return dt;
}
function fmtISO(d) {
    return new Date(d).toISOString().slice(0, 10);
}
function addDays(d, n) {
    const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt;
}

export default function ShiftPlannerPage() {
    const { token, user } = useAuth();
    const isAdmin = user?.role === "admin";

    const [view, setView] = useState("week");        // 'week' | 'month' | 'list'
    const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
    const [shifts, setShifts] = useState([]);
    const [usersMap, setUsersMap] = useState({});
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null); // { user_id?, date?, shift? }

    // Calculate the date range for the API based on current view
    const { dateFrom, dateTo } = useMemo(() => {
        const a = new Date(anchor);
        if (view === "month") {
            const first = new Date(a.getFullYear(), a.getMonth(), 1);
            const last = new Date(a.getFullYear(), a.getMonth() + 1, 0);
            return { dateFrom: fmtISO(first), dateTo: fmtISO(last) };
        }
        if (view === "list") {
            return { dateFrom: fmtISO(a), dateTo: fmtISO(addDays(a, 30)) };
        }
        return { dateFrom: fmtISO(a), dateTo: fmtISO(addDays(a, 6)) };
    }, [anchor, view]);

    const fetchShifts = async () => {
        if (!token) return;
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${API}/api/shifts?from=${dateFrom}&to=${dateTo}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setShifts(data.shifts || []);
            setUsersMap(data.users || {});
        } catch (e) {
            console.error("fetchShifts", e);
            toast.error("Schichten konnten nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        if (!isAdmin || !token) return;
        try {
            const { data } = await axios.get(`${API}/api/users`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAllUsers((data.users || data || []).filter((u) => u.active !== false));
        } catch (e) {
            console.error("fetchUsers", e);
        }
    };

    useEffect(() => { fetchShifts(); /* eslint-disable-next-line */ }, [dateFrom, dateTo, token]);
    useEffect(() => { fetchUsers(); /* eslint-disable-next-line */ }, [token]);

    const openNew = (presets = {}) => {
        setEditing({ ...presets });
        setDialogOpen(true);
    };
    const openEdit = (shift) => {
        setEditing({ shift });
        setDialogOpen(true);
    };

    const navTitle = useMemo(() => {
        const a = new Date(anchor);
        if (view === "month") {
            return a.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
        }
        if (view === "list") {
            return `Nächste 30 Tage ab ${a.toLocaleDateString("de-DE")}`;
        }
        const end = addDays(a, 6);
        return `${a.toLocaleDateString("de-DE", { day: "2-digit", month: "short" })} – ${end.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}`;
    }, [anchor, view]);

    const navPrev = () => {
        if (view === "month") {
            const a = new Date(anchor); a.setMonth(a.getMonth() - 1); setAnchor(a);
        } else if (view === "list") {
            setAnchor(addDays(anchor, -30));
        } else {
            setAnchor(addDays(anchor, -7));
        }
    };
    const navNext = () => {
        if (view === "month") {
            const a = new Date(anchor); a.setMonth(a.getMonth() + 1); setAnchor(a);
        } else if (view === "list") {
            setAnchor(addDays(anchor, 30));
        } else {
            setAnchor(addDays(anchor, 7));
        }
    };
    const navToday = () => {
        setAnchor(view === "month" ? new Date(new Date().getFullYear(), new Date().getMonth(), 1) : startOfWeek(new Date()));
    };

    return (
        <div className="space-y-5" data-testid="shift-planner-page">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Schichtplan</h1>
                    <p className="text-sm text-neutral-500 mt-1">
                        {isAdmin
                            ? "Plane Schichten für deine Mitarbeiter — Soll/Ist-Vergleich automatisch."
                            : "Deine geplanten Schichten."}
                    </p>
                </div>
                {isAdmin && (
                    <Button data-testid="shift-new-btn" onClick={() => openNew()} className="bg-neutral-900 hover:bg-neutral-800">
                        <Plus className="w-4 h-4 mr-1.5" /> Neue Schicht
                    </Button>
                )}
            </header>

            {/* Navigation toolbar */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex rounded-md border border-neutral-200 overflow-hidden">
                    <button
                        onClick={() => setView("week")}
                        data-testid="shift-view-week"
                        className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${view === "week" ? "bg-neutral-900 text-white" : "bg-white hover:bg-neutral-50"}`}
                    >
                        <Grid3x3 className="w-3.5 h-3.5" /> Woche
                    </button>
                    <button
                        onClick={() => setView("month")}
                        data-testid="shift-view-month"
                        className={`px-3 py-1.5 text-sm flex items-center gap-1.5 border-l border-neutral-200 ${view === "month" ? "bg-neutral-900 text-white" : "bg-white hover:bg-neutral-50"}`}
                    >
                        <CalendarIcon className="w-3.5 h-3.5" /> Monat
                    </button>
                    <button
                        onClick={() => setView("list")}
                        data-testid="shift-view-list"
                        className={`px-3 py-1.5 text-sm flex items-center gap-1.5 border-l border-neutral-200 ${view === "list" ? "bg-neutral-900 text-white" : "bg-white hover:bg-neutral-50"}`}
                    >
                        <List className="w-3.5 h-3.5" /> Liste
                    </button>
                </div>

                <div className="inline-flex items-center gap-1">
                    <Button variant="outline" size="sm" onClick={navPrev} data-testid="shift-nav-prev">
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={navToday} data-testid="shift-nav-today">Heute</Button>
                    <Button variant="outline" size="sm" onClick={navNext} data-testid="shift-nav-next">
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                </div>

                <div className="text-sm font-medium text-neutral-700" data-testid="shift-nav-title">
                    {navTitle}
                </div>
            </div>

            {/* View */}
            <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                {loading && (
                    <div className="p-8 text-center text-sm text-neutral-500">Lade Schichten …</div>
                )}
                {!loading && view === "week" && (
                    <ShiftWeekGrid
                        weekStart={anchor}
                        shifts={shifts}
                        users={isAdmin ? allUsers : [user]}
                        isAdmin={isAdmin}
                        onCreate={openNew}
                        onEdit={openEdit}
                    />
                )}
                {!loading && view === "month" && (
                    <ShiftMonthCalendar
                        monthStart={anchor}
                        shifts={shifts}
                        usersMap={usersMap}
                        isAdmin={isAdmin}
                        onCreate={openNew}
                        onEdit={openEdit}
                    />
                )}
                {!loading && view === "list" && (
                    <ShiftListView
                        shifts={shifts}
                        usersMap={usersMap}
                        isAdmin={isAdmin}
                        onEdit={openEdit}
                        onRefresh={fetchShifts}
                        token={token}
                    />
                )}
            </div>

            {dialogOpen && (
                <ShiftDialog
                    open={dialogOpen}
                    onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}
                    initial={editing}
                    users={allUsers}
                    token={token}
                    onSaved={() => { setDialogOpen(false); setEditing(null); fetchShifts(); }}
                />
            )}
        </div>
    );
}
